import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  defaultSupplierFormValues,
  supplierFormSchema,
  SUPPLIER_COUNTRY_OPTIONS,
  SUPPLIER_CURRENCY_OPTIONS,
  type SupplierFormValues,
} from "@/pages/suppliers/supplier-form-shared";
import { getRucInfoForSupplier } from "@/services/supplierService";

export interface SupplierFormProps {
  initialValues?: Partial<SupplierFormValues>;
  submitLabel: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (values: SupplierFormValues) => void;
}

/**
 * Shared form body for create + edit. Owns the RUT lookup side effect
 * so both pages get identical "type a 12-digit RUT → pre-fill legal
 * name/address/email" behavior.
 */
export function SupplierForm({
  initialValues,
  submitLabel,
  isSubmitting,
  onCancel,
  onSubmit,
}: SupplierFormProps): JSX.Element {
  const { t } = useTranslation();
  const [rucInfoLoading, setRucInfoLoading] = useState(false);
  const prefilledFromRucRef = useRef<string | null>(null);

  const form = useForm<SupplierFormValues>({
    resolver: zodResolver(supplierFormSchema),
    defaultValues: { ...defaultSupplierFormValues, ...initialValues },
  });

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const documentType = watch("documentType");
  const countryCode = watch("countryCode");
  const rutValue = watch("rut");

  // Keep documentType in sync with country selection: any country != UY
  // forces EXT, switching back to UY restores RUT default.
  useEffect(() => {
    if (documentType === "EXT" && countryCode === "UY") {
      setValue("documentType", "RUT");
    } else if (documentType !== "EXT" && countryCode && countryCode !== "UY") {
      setValue("documentType", "EXT");
      setValue("rut", "");
    }
  }, [countryCode, documentType, setValue]);

  const fetchRucInfo = useCallback(() => {
    if (documentType !== "RUT") return;
    const digits = (rutValue ?? "").replace(/\D/g, "");
    if (digits.length !== 12) return;
    const normalized = digits.padStart(12, "0").slice(-12);
    setRucInfoLoading(true);
    getRucInfoForSupplier(normalized)
      .then((res) => {
        if (!res.valid) {
          toast.error(
            t(
              "suppliers.form.rucInfoInvalid",
              "No pudimos cargar los datos del RUT.",
            ),
          );
          return;
        }
        prefilledFromRucRef.current = normalized;
        if (res.legalName?.trim()) {
          setValue("legalName", res.legalName.trim());
        }
        if (res.legalAddress?.trim()) {
          setValue("address", res.legalAddress.trim());
        }
        if (res.city?.trim()) setValue("ciudad", res.city.trim());
        if (res.department?.trim())
          setValue("departamento", res.department.trim());
        if (res.email?.trim()) setValue("email", res.email.trim());
        if (res.phone?.trim()) setValue("phone", res.phone.trim());
      })
      .catch(() => {
        toast.error(
          t(
            "suppliers.form.rucInfoError",
            "Error al consultar el RUT. Probá de nuevo.",
          ),
        );
      })
      .finally(() => setRucInfoLoading(false));
  }, [documentType, rutValue, setValue, t]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("suppliers.form.identity", "Identidad")}
          </CardTitle>
          <CardDescription>
            {t(
              "suppliers.form.identityHint",
              "Si conocés el RUT, ingresalo y consultamos DGI para autocompletar.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplier-country">
              {t("suppliers.form.country", "País")}
            </Label>
            <Controller
              name="countryCode"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || "UY"}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger id="supplier-country" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPLIER_COUNTRY_OPTIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.countryCode ? (
              <p className="text-xs text-destructive">
                {errors.countryCode.message}
              </p>
            ) : null}
          </div>

          {documentType !== "EXT" ? (
            <div className="space-y-2">
              <Label htmlFor="supplier-rut">
                {documentType === "CI"
                  ? t("suppliers.form.ci", "Cédula (CI)")
                  : t("suppliers.form.rut", "RUT (12 dígitos)")}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="supplier-rut"
                  inputMode="numeric"
                  placeholder={documentType === "CI" ? "12345678" : "210000000000"}
                  {...register("rut")}
                  onBlur={() => {
                    if (documentType === "RUT") fetchRucInfo();
                  }}
                />
                {documentType === "RUT" ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fetchRucInfo()}
                    disabled={rucInfoLoading}
                  >
                    {rucInfoLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t("suppliers.form.rucLookup", "Consultar DGI")
                    )}
                  </Button>
                ) : null}
              </div>
              {errors.rut ? (
                <p className="text-xs text-destructive">{errors.rut.message}</p>
              ) : null}
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="supplier-doc-ext">
                {t("suppliers.form.docExt", "Documento extranjero")}
              </Label>
              <Input
                id="supplier-doc-ext"
                placeholder={t(
                  "suppliers.form.docExtPlaceholder",
                  "Pasaporte / Tax ID",
                )}
                {...register("docRecepExt")}
              />
              {errors.docRecepExt ? (
                <p className="text-xs text-destructive">
                  {errors.docRecepExt.message}
                </p>
              ) : null}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="supplier-legal-name">
              {t("suppliers.form.legalName", "Razón social")}
            </Label>
            <Input
              id="supplier-legal-name"
              {...register("legalName")}
            />
            {errors.legalName ? (
              <p className="text-xs text-destructive">
                {errors.legalName.message}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="supplier-display-name">
              {t("suppliers.form.displayName", "Nombre comercial (opcional)")}
            </Label>
            <Input
              id="supplier-display-name"
              {...register("displayName")}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("suppliers.form.contact", "Contacto y ubicación")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplier-address">
              {t("suppliers.form.address", "Dirección")}
            </Label>
            <Input id="supplier-address" {...register("address")} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="supplier-ciudad">
                {t("suppliers.form.ciudad", "Ciudad")}
              </Label>
              <Input id="supplier-ciudad" {...register("ciudad")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier-departamento">
                {t("suppliers.form.departamento", "Departamento")}
              </Label>
              <Input
                id="supplier-departamento"
                {...register("departamento")}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="supplier-email">
                {t("suppliers.form.email", "Email")}
              </Label>
              <Input
                id="supplier-email"
                type="email"
                {...register("email")}
              />
              {errors.email ? (
                <p className="text-xs text-destructive">
                  {errors.email.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier-phone">
                {t("suppliers.form.phone", "Teléfono")}
              </Label>
              <Input id="supplier-phone" {...register("phone")} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("suppliers.form.preferences", "Preferencias")}
          </CardTitle>
          <CardDescription>
            {t(
              "suppliers.form.preferencesHint",
              "Defaults que aplicamos cuando registrás un gasto manual o procesamos un EnvioCFE de este proveedor.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="supplier-default-currency">
                {t(
                  "suppliers.form.defaultCurrency",
                  "Moneda por defecto",
                )}
              </Label>
              <Controller
                name="defaultCurrency"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? "none"}
                    onValueChange={(v) =>
                      field.onChange(v === "none" ? undefined : v)
                    }
                  >
                    <SelectTrigger
                      id="supplier-default-currency"
                      className="w-full"
                    >
                      <SelectValue
                        placeholder={t(
                          "suppliers.form.defaultCurrencyNone",
                          "Sin preferencia",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        {t(
                          "suppliers.form.defaultCurrencyNone",
                          "Sin preferencia",
                        )}
                      </SelectItem>
                      {SUPPLIER_CURRENCY_OPTIONS.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="supplier-default-category">
                {t(
                  "suppliers.form.defaultCategory",
                  "Categoría de gasto",
                )}
              </Label>
              <Input
                id="supplier-default-category"
                placeholder={t(
                  "suppliers.form.defaultCategoryPlaceholder",
                  "Software, Servicios públicos, …",
                )}
                {...register("defaultCategory")}
              />
            </div>
          </div>
          <div className="flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/30 p-4">
            <div className="space-y-1">
              <Label
                htmlFor="supplier-auto-ack"
                className="text-sm font-medium text-foreground"
              >
                {t(
                  "suppliers.form.autoAck",
                  "Aceptar automáticamente comprobantes recibidos",
                )}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t(
                  "suppliers.form.autoAckHint",
                  "Cuando recibís un comprobante de este proveedor, lo marcamos como aceptado sin pedir confirmación. Podés desactivarlo en cualquier momento.",
                )}
              </p>
            </div>
            <Controller
              name="autoAckCfeOnReceive"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="supplier-auto-ack"
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked === true)}
                  className="mt-0.5 h-5 w-5"
                />
              )}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="supplier-notes">
              {t("suppliers.form.notes", "Notas")}
            </Label>
            <Textarea
              id="supplier-notes"
              rows={3}
              {...register("notes")}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("suppliers.form.cancel", "Cancelar")}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

export type { UseFormReturn };
